import { Injectable } from '@angular/core';
import { Observable, EMPTY } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import { ProjectService } from '@app/core/services/project.service';
import {
  AddProject,
  AddProjectSuccess,
  LoadProject,
  LoadProjectSuccess,
  LoadProjects,
  LoadProjectsSuccess,
  LoadProjectUserReport,
  LoadProjectUserReportSuccess,
  ProjectActionTypes,
  UpdateProject,
  UpdateProjectSuccess,
} from '@dwo/actions';

@Injectable()
export class ProjectEffect {
  constructor(private actions$: Actions, private projectService: ProjectService) {}

  addProject$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<AddProject>(ProjectActionTypes.AddProject),
        switchMap((action: AddProject) =>
          this.projectService.addProject(action.project).pipe(
            map(() => new AddProjectSuccess()),
            catchError(() => EMPTY),
          ),
        ),
      ),
  );

  addProjectSuccess$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<AddProjectSuccess>(ProjectActionTypes.AddProjectSuccess),
        map(() => new LoadProjects()),
      ),
  );

  updateProject$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<UpdateProject>(ProjectActionTypes.UpdateProject),
        switchMap((action: UpdateProject) =>
          this.projectService.updateProject(action.project).pipe(
            map(() => new UpdateProjectSuccess(action.project)),
            catchError(() => EMPTY),
          ),
        ),
      ),
  );

  updateProjectSuccess$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<UpdateProjectSuccess>(ProjectActionTypes.UpdateProjectSuccess),
        switchMap((action: UpdateProjectSuccess) => [new LoadProjects(), new LoadProject(action.project.id)]),
      ),
  );

  loadProjects$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<LoadProjects>(ProjectActionTypes.LoadProjects),
        exhaustMap(() =>
          this.projectService.loadProjects().pipe(
            map((projects) => new LoadProjectsSuccess(projects)),
            catchError(() => EMPTY),
          ),
        ),
      ),
  );

  loadProject$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<LoadProject>(ProjectActionTypes.LoadProject),
        switchMap((action: LoadProject) =>
          this.projectService.loadProject(action.projectId).pipe(
            map((project) => new LoadProjectSuccess(project)),
            catchError(() => EMPTY),
          ),
        ),
      ),
  );

  loadProjectUserReport$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<LoadProjectUserReport>(ProjectActionTypes.LoadProjectUserReport),
        switchMap(() =>
          this.projectService.loadProjectUserReport().pipe(
            map((report) => new LoadProjectUserReportSuccess(report)),
            catchError(() => EMPTY),
          ),
        ),
      ),
  );
}
